import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Space, Typography, message, Spin } from 'antd';
import { SaveOutlined, EyeOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { visionMissionAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function VisionMission() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [dataId, setDataId] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await visionMissionAPI.get();
            if (response.data) {
                form.setFieldsValue(response.data);
                setDataId(response.data.id || response.data._id);
            }
        } catch (error) {
            console.error('Error fetching vision & mission data:', error);
            message.error('Failed to fetch vision & mission data');
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            if (dataId) {
                await visionMissionAPI.update(dataId, values);
                message.success('Vision & Mission updated successfully!');
            } else {
                const response = await visionMissionAPI.create(values);
                setDataId(response.data.id || response.data._id);
                message.success('Vision & Mission created successfully!');
            }
            fetchData();
        } catch (error) {
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}><EyeOutlined /> Vision & Mission</Title>
                <Text type="secondary">Manage company vision, mission, and core values</Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ mission: [''], values: [{ title: '', description: '', icon: '' }] }}>
                {/* Vision */}
                <Card title="🎯 Vision" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="vision" label="Company Vision" rules={[{ required: true }]}>
                        <TextArea rows={4} placeholder="Enter company vision..." />
                    </Form.Item>
                </Card>

                {/* Mission */}
                <Card title="🚀 Mission" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="mission">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                        <Text strong style={{ minWidth: 80 }}>Mission {index + 1}</Text>
                                        <Form.Item {...field} style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Please enter mission' }]}>
                                            <Input size="large" placeholder="Mission statement" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)}>Remove</Button>
                                        )}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add Mission
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Values */}
                <Card title="💎 Company Values" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="values">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Card key={field.key} size="small" style={{ marginBottom: 16, background: '#f5f5f5' }} title={`Value ${index + 1}`}>
                                        <Form.Item {...field} name={[field.name, 'title']} label="Title" rules={[{ required: true }]}>
                                            <Input size="large" placeholder="e.g., Integrity" />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'description']} label="Description" rules={[{ required: true }]}>
                                            <TextArea rows={3} placeholder="Describe this value..." />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'icon']} label="Icon (emoji or URL)">
                                            <Input size="large" placeholder="🎯 or icon URL" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} block>
                                                Remove Value
                                            </Button>
                                        )}
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add Value
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Submit Button */}
                <Form.Item style={{ marginTop: 32 }}>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        block
                        style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            border: 'none',
                            height: 48
                        }}
                    >
                        Save Vision & Mission
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
